import { useEffect, useState } from "react";
import AdminLayout from "../layout/AdminLayout";
import { API_BASE_URL } from "../../config/api";
import { adminAuthHeader } from "../../utils/authHeader";
import FraudCharts from "../components/FraudCharts";

const StatCard = ({ label, value, sub, color, bg, icon }) => (
  <div style={{ background: "#fff", borderRadius: "16px", padding: "20px", boxShadow: "0 2px 10px rgba(0,0,0,0.05)", display: "flex", alignItems: "flex-start", gap: "14px" }}>
    <div style={{ width: "50px", height: "50px", borderRadius: "14px", background: bg, display: "flex", alignItems: "center", justifyContent: "center", fontSize: "22px", color, flexShrink: 0 }}>{icon}</div>
    <div style={{ flex: 1, minWidth: 0 }}>
      <p style={{ margin: 0, fontSize: "12px", color: "#a3aed0", fontWeight: "600", textTransform: "uppercase", letterSpacing: "0.5px" }}>{label}</p>
      <h3 style={{ margin: "2px 0 0", fontSize: "26px", fontWeight: "800", color: "#1b2559" }}>{value ?? "—"}</h3>
      {sub && <p style={{ margin: "2px 0 0", fontSize: "12px", color: "#6b7280" }}>{sub}</p>}
    </div>
  </div>
);

const AdminDashboard = () => {
  const [stats,   setStats]   = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API_BASE_URL}/api/admin/dashboard`, { headers: adminAuthHeader() })
      .then(r => r.json())
      .then(d => { setStats(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  if (loading) return (
    <AdminLayout>
      <div style={{ textAlign: "center", padding: "80px", color: "#a3aed0" }}>
        <div style={{ fontSize: "32px", marginBottom: "12px" }}>⏳</div>
        Loading dashboard…
      </div>
    </AdminLayout>
  );

  return (
    <AdminLayout>
      <div className="admin-header">
        <p className="admin-welcome">Welcome Admin</p>
        <h2 className="admin-title">Dashboard Overview</h2>
      </div>

      {/* ── FRAUD STATS ── */}
      <div style={{ marginBottom: "10px" }}>
        <p style={{ fontSize: "12px", fontWeight: "700", color: "#a3aed0", textTransform: "uppercase", letterSpacing: "0.5px", margin: "0 0 12px" }}>Fraud Reports</p>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: "14px" }}>
          <StatCard label="Total Frauds"   value={stats?.totalFrauds}  sub={`${stats?.approvalRate ?? 0}% approval rate`}                icon="🚨" color="#4318ff" bg="#f0f4ff" />
          <StatCard label="Pending Review" value={stats?.pending}       sub="Awaiting admin action"                                       icon="⏳" color="#d97706" bg="#fffbeb" />
          <StatCard label="Approved"       value={stats?.approved}      sub="Visible to all owners"                                       icon="✅" color="#16a34a" bg="#f0fdf4" />
          <StatCard label="Rejected"       value={stats?.rejected}      sub="Not enough proof"                                            icon="❌" color="#dc2626" bg="#fee2e2" />
          <StatCard label="Under Review"   value={stats?.under_review}  sub="Being investigated"                                          icon="🔍" color="#0891b2" bg="#ecfeff" />
          <StatCard label="This Month"     value={stats?.thisMonth}
            sub={stats?.monthGrowth > 0 ? `↑ ${stats.monthGrowth}% vs last month` : stats?.monthGrowth < 0 ? `↓ ${Math.abs(stats.monthGrowth)}% vs last month` : "Same as last month"}
            icon="📅" color="#7c3aed" bg="#f5f3ff" />
        </div>
      </div>

      {/* ── OWNER STATS ── */}
      <div style={{ marginBottom: "24px", marginTop: "24px" }}>
        <p style={{ fontSize: "12px", fontWeight: "700", color: "#a3aed0", textTransform: "uppercase", letterSpacing: "0.5px", margin: "0 0 12px" }}>Owners</p>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: "14px" }}>
          <StatCard label="Total Owners"  value={stats?.totalOwners}  sub="All registered"             icon="👥" color="#4318ff" bg="#f0f4ff" />
          <StatCard label="Active Owners" value={stats?.activeOwners} sub="Not blocked"                 icon="✅" color="#16a34a" bg="#f0fdf4" />
          <StatCard label="Blocked"       value={stats?.blockedOwners} sub="Suspended accounts"         icon="🚫" color="#dc2626" bg="#fee2e2" />
        </div>
      </div>

      {/* ── TOP FRAUD TYPES + TOP CITIES ── */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: "16px", marginBottom: "24px" }}>

        {/* Top fraud types */}
        <div style={{ background: "#fff", borderRadius: "16px", padding: "20px", boxShadow: "0 2px 10px rgba(0,0,0,0.05)" }}>
          <p style={{ margin: "0 0 16px", fontWeight: "700", color: "#1b2559", fontSize: "14px" }}>🏆 Top Fraud Types</p>
          {stats?.topFraudTypes?.length ? stats.topFraudTypes.map((item, i) => (
            <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "10px" }}>
              <div style={{ flex: 1, marginRight: "12px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "4px" }}>
                  <span style={{ fontSize: "13px", color: "#374151", fontWeight: "500" }}>{item._id}</span>
                  <span style={{ fontSize: "12px", color: "#6b7280" }}>{item.count}</span>
                </div>
                <div style={{ height: "6px", background: "#f3f4f6", borderRadius: "4px", overflow: "hidden" }}>
                  <div style={{ height: "100%", width: `${Math.min(100, (item.count / (stats.topFraudTypes[0]?.count || 1)) * 100)}%`, background: "#4318ff", borderRadius: "4px", transition: "width 0.6s" }} />
                </div>
              </div>
            </div>
          )) : <p style={{ color: "#a3aed0", fontSize: "13px" }}>No data yet</p>}
        </div>

        {/* Top cities */}
        <div style={{ background: "#fff", borderRadius: "16px", padding: "20px", boxShadow: "0 2px 10px rgba(0,0,0,0.05)" }}>
          <p style={{ margin: "0 0 16px", fontWeight: "700", color: "#1b2559", fontSize: "14px" }}>📍 Top Cities by Fraud Reports</p>
          {stats?.topCities?.length ? stats.topCities.map((item, i) => (
            <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "10px" }}>
              <div style={{ flex: 1, marginRight: "12px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "4px" }}>
                  <span style={{ fontSize: "13px", color: "#374151", fontWeight: "500" }}>{item._id || "Unknown"}</span>
                  <span style={{ fontSize: "12px", color: "#6b7280" }}>{item.count}</span>
                </div>
                <div style={{ height: "6px", background: "#f3f4f6", borderRadius: "4px", overflow: "hidden" }}>
                  <div style={{ height: "100%", width: `${Math.min(100, (item.count / (stats.topCities[0]?.count || 1)) * 100)}%`, background: "#ef4444", borderRadius: "4px", transition: "width 0.6s" }} />
                </div>
              </div>
            </div>
          )) : <p style={{ color: "#a3aed0", fontSize: "13px" }}>No data yet</p>}
        </div>
      </div>

      {/* ── RECENT ACTIVITY ── */}
      {stats?.recentActivity?.length > 0 && (
        <div style={{ background: "#fff", borderRadius: "16px", padding: "20px", boxShadow: "0 2px 10px rgba(0,0,0,0.05)", marginBottom: "24px" }}>
          <p style={{ margin: "0 0 16px", fontWeight: "700", color: "#1b2559", fontSize: "14px" }}>🕐 Recent Reports</p>
          <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
            {stats.recentActivity.map((r, i) => (
              <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 14px", background: "#f9faff", borderRadius: "10px", flexWrap: "wrap", gap: "6px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                  <span style={{ fontSize: "13px", fontWeight: "600", color: "#1b2559" }}>{r.fullName}</span>
                  <span style={{ fontSize: "11px", background: "#fee2e2", color: "#991b1b", padding: "2px 8px", borderRadius: "10px" }}>{r.fraudType}</span>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                  <span style={{ fontSize: "12px", color: "#6b7280" }}>by {r.reportedBy?.name || "Unknown"}</span>
                  <span style={{ fontSize: "11px", color: "#a3aed0" }}>{new Date(r.createdAt).toLocaleDateString("en-IN")}</span>
                  <span style={{ fontSize: "11px", background: r.status === "approved" ? "#f0fdf4" : r.status === "pending" ? "#fffbeb" : "#f3f4f6", color: r.status === "approved" ? "#166534" : r.status === "pending" ? "#92400e" : "#374151", padding: "2px 8px", borderRadius: "10px", fontWeight: "600" }}>
                    {r.status}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── CHARTS ── */}
      <FraudCharts />
    </AdminLayout>
  );
};

export default AdminDashboard;
